import { apiClient } from './apiClient';

import type { ApiResponse } from '@kent/shared';

export interface Prompt {
  promptId: string;
  text: string;
  maxDurationSec: number;
  order: number;
  videoPlaybackUrl?: string | null;
  videoPlaybackExpiresIn?: number | null;
  videoDurationSec?: number | null;
}

export interface PromptSet {
  promptSetId: string;
  name: string;
  prompts: Prompt[];
  retakePolicy: 'unlimited' | 'once' | 'none';
  maxDurationSec: number;
}

export interface VideoResponseData {
  videoResponseId: string;
  promptId: string;
  durationSec?: number;
  fileUri?: string;
  createdAt: string;
}

export interface VideoInterviewData {
  videoInterviewId: string;
  applicationId: string;
  status: string;
  accommodationType: string;
  accommodationNotes?: string;
  durationTotalSec?: number;
  submittedAt?: string;
  createdAt: string;
  promptSet?: PromptSet;
  responses: VideoResponseData[];
}

export interface UploadUrlData {
  uploadUrl: string;
  fileKey: string;
  expiresIn: number;
}

interface InitiateMultipartData {
  uploadId: string;
  fileKey: string;
}

interface PartUrlItem {
  partNumber: number;
  url: string;
  expiresIn: number;
}

interface PartUrlsData {
  urls: PartUrlItem[];
}

export const videoApi = {
  /**
   * Get the currently active prompt set with prompts array.
   */
  getActivePromptSet: () => apiClient.get<ApiResponse<PromptSet | null>>('/prompt-sets/active'),

  /**
   * Start a new video interview session.
   */
  startInterview: (data: {
    applicationId: string;
    promptSetId?: string;
    accommodationType?: 'none' | 'text' | 'audio' | 'live';
    accommodationNotes?: string;
  }) => apiClient.post<ApiResponse<VideoInterviewData>>('/video/interviews', data),

  /**
   * Get interview status and responses.
   */
  getInterview: (interviewId: string) =>
    apiClient.get<ApiResponse<VideoInterviewData>>(`/video/interviews/${interviewId}`),

  /**
   * Get a presigned S3 upload URL for a video response.
   */
  getUploadUrl: (interviewId: string, data: { promptId: string; contentType: string }) =>
    apiClient.post<ApiResponse<UploadUrlData>>(`/video/interviews/${interviewId}/upload-url`, data),

  /**
   * Confirm that a video response was uploaded to S3.
   */
  confirmResponse: (
    interviewId: string,
    data: {
      promptId: string;
      fileKey: string;
      durationSec?: number;
      checksum?: string;
    },
  ) => apiClient.post<ApiResponse<VideoResponseData>>(`/video/interviews/${interviewId}/responses`, data),

  /**
   * Submit the completed video interview.
   */
  submitInterview: (interviewId: string) =>
    apiClient.post<ApiResponse<VideoInterviewData>>(`/video/interviews/${interviewId}/submit`),

  /**
   * Initiate a multipart upload for a large video file.
   */
  initiateMultipart: (interviewId: string, data: { filename: string; contentType: string }) =>
    apiClient.post<ApiResponse<InitiateMultipartData>>(`/video/interviews/${interviewId}/multipart/initiate`, data),

  /**
   * Get presigned URLs for multipart upload parts.
   */
  getPartUrls: (interviewId: string, data: { uploadId: string; fileKey: string; partNumbers: number[] }) =>
    apiClient.post<ApiResponse<PartUrlsData>>(`/video/interviews/${interviewId}/multipart/urls`, data),

  /**
   * Complete a multipart upload by assembling all parts.
   */
  completeMultipart: (
    interviewId: string,
    data: { uploadId: string; fileKey: string; parts: Array<{ partNumber: number; etag: string }> },
  ) => apiClient.post<ApiResponse<{ fileKey: string }>>(`/video/interviews/${interviewId}/multipart/complete`, data),

  /**
   * Abort a multipart upload (cleanup on failure).
   */
  abortMultipart: (interviewId: string, data: { uploadId: string; fileKey: string }) =>
    apiClient.post<ApiResponse<null>>(`/video/interviews/${interviewId}/multipart/abort`, data),
};

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_RETRY_ATTEMPTS = 3;
const BASE_RETRY_DELAY_MS = 1000;

function uploadPartToS3(url: string, chunk: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader('ETag');
        if (!etag) {
          reject(new Error('Missing ETag in S3 response'));
          return;
        }
        resolve(etag);
      } else {
        reject(new Error(`Part upload failed with status ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Part upload network error'));
    xhr.onabort = () => reject(new Error('Part upload cancelled'));
    xhr.open('PUT', url);
    xhr.send(chunk);
  });
}

async function uploadPartWithRetry(url: string, chunk: Blob): Promise<string> {
  let lastError: Error | undefined;

  for (let attempt = 0; attempt < MAX_RETRY_ATTEMPTS; attempt++) {
    try {
      return await uploadPartToS3(url, chunk);
    } catch (err) {
      lastError = err instanceof Error ? err : new Error('Unknown upload error');
      if (attempt < MAX_RETRY_ATTEMPTS - 1) {
        const delay = BASE_RETRY_DELAY_MS * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError ?? new Error('Part upload failed after retries');
}

export async function uploadVideoChunked(
  interviewId: string,
  file: Blob,
  onProgress?: (percent: number) => void,
): Promise<string> {
  // For small files, fall back to single presigned URL upload
  if (file.size < CHUNK_SIZE) {
    const urlResponse = await videoApi.getUploadUrl(interviewId, {
      promptId: 'chunked',
      contentType: file.type || 'video/webm',
    });
    const { uploadUrl, fileKey } = urlResponse.data;

    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress?.(Math.round((e.loaded / e.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error('Upload failed'));
        }
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'video/webm');
      xhr.send(file);
    });

    onProgress?.(100);
    return fileKey;
  }

  // Chunked multipart upload for large files
  const totalParts = Math.ceil(file.size / CHUNK_SIZE);

  // Step 1: Initiate multipart upload
  const initResponse = await videoApi.initiateMultipart(interviewId, {
    filename: 'response.webm',
    contentType: file.type || 'video/webm',
  });
  const { uploadId, fileKey } = initResponse.data;

  // Step 2: Get presigned URLs for all parts
  const partNumbers = Array.from({ length: totalParts }, (_, i) => i + 1);
  const urlsResponse = await videoApi.getPartUrls(interviewId, {
    uploadId,
    fileKey,
    partNumbers,
  });

  // Step 3: Upload each chunk with retry
  const completedParts: Array<{ partNumber: number; etag: string }> = [];

  try {
    for (let i = 0; i < totalParts; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const partUrl = urlsResponse.data.urls.find((u) => u.partNumber === i + 1);
      if (!partUrl) {
        throw new Error(`Missing presigned URL for part ${i + 1}`);
      }

      const etag = await uploadPartWithRetry(partUrl.url, chunk);
      completedParts.push({ partNumber: i + 1, etag });

      onProgress?.(Math.round(((i + 1) / totalParts) * 100));
    }
  } catch (err) {
    // Abort the multipart upload on failure
    try {
      await videoApi.abortMultipart(interviewId, { uploadId, fileKey });
    } catch {
      // Best-effort abort; ignore errors
    }
    throw err;
  }

  // Step 4: Complete multipart upload
  await videoApi.completeMultipart(interviewId, {
    uploadId,
    fileKey,
    parts: completedParts,
  });

  return fileKey;
}
